import { combineReducers } from 'redux';
import survey, * as fromSurvey from '../_slices/surveys_slice';
import authentication, * as fromAuthentication from './authentication_reducer';
import enrichment_center, * as fromEnrichmentCenter from './enrichment_center_reducer';
import stressor_categories from "../_slices/stressor_categories_slice";
import groups, * as fromGroups from "../_slices/groups_slice";
import feedback from './feedback_reducer';
import insights from '../_slices/insights_slice';
import cme_credits from "../_slices/cme_credits_slice";
import { LOGOUT } from "../_actions/_types.actions";
import {createSelector} from "reselect";


const appReducer = combineReducers({
  authentication,
  survey,
  enrichment_center,
  feedback,
  groups,
  insights,
  stressor_categories,
  cme_credits,
});
// why this "rootReducer" is declared here alongside "appReducer": https://alligator.io/redux/reset-state-redux/
const rootReducer = (state, action) => {
  if (action.type === LOGOUT) {
    // RESET REDUX STATE ON LOGOUT
    localStorage.removeItem('currentBlock');
    localStorage.removeItem('currentSession');
    localStorage.removeItem('currentGroup');
    state = undefined;
  }
  return appReducer(state, action);
};

export default rootReducer;

/*
-------------------------------
SELECTOR FUNCTIONS REDECLARED
why these functions are redeclared here:
https://egghead.io/lessons/javascript-redux-colocating-selectors-with-reducers
-------------------------------
*/

export const getDemographicQuestions = (state) =>
  fromSurvey.getDemographicQuestions(state.survey)

export const getChoices = (state, question) =>
  fromSurvey.getChoices(state.survey, question);

export const getBurnoutQuestion = (state) =>
  fromSurvey.getBurnoutQuestion(state.survey);

export const getMultipleChoiceQuestionsForCharts = (state) =>
  fromSurvey.getMultipleChoiceQuestionsForCharts(state.survey);

export const getAllQuestions = (state) =>
  fromSurvey.getAllQuestions(state.survey);

export const getAgreeDisagreeQuestions = (state) =>
  fromSurvey.getAgreeDisagreeQuestions(state.survey);

export const getFreeTextQuestionsForCharts = (state) =>
  fromSurvey.getFreeTextQuestionsForCharts(state.survey);

// export const getHoursWorkedQuestion = (state) =>
//   fromSurvey.getHoursWorkedQuestion(state.survey);

export const getCurrentUser = (state) =>
  fromAuthentication.getCurrentUser(state.authentication)

export const loggedIn = (state) =>
  fromAuthentication.loggedIn(state.authentication);

export const isAdmin = (state) =>
  fromAuthentication.isAdmin(state.authentication);

export const isOrgLeader = (state) =>
  fromAuthentication.isOrgLeader(state.authentication);

export const canSeeLocations = (state) =>
  fromAuthentication.canSeeLocations(state.authentication);

export const canSeeCompletionStatistics = (state) =>
  fromAuthentication.canSeeCompletionStatistics(state.authentication);

export const canSeeResults = (state) =>
  fromAuthentication.canSeeResults(state.authentication)

export const isDemoUser = (state) =>
  fromAuthentication.isDemoUser(state.authentication);

export const showBurnoutAssessment = (state) =>
  fromAuthentication.showBurnoutAssessment(state.authentication);

const selectAuthentication = (state) => state.authentication;

export const getCredentials = createSelector(
  [selectAuthentication],
  (authentication) => {
    const { accessToken, client, email } = authentication;
    return { accessToken, client, email };
  }
);

export const getLogoUrl = (state) => {
  let logoUrl;
  if (isAdmin(state)) {
    logoUrl = fromGroups.selectCurrentGroupLogoUrl(state);
  } else {
    logoUrl = fromAuthentication.getLogoUrl(state.authentication);
  }
  return logoUrl;
};

export const getGroupName = (state) => {
  let groupName;
  if (isAdmin(state)) {
    groupName = fromGroups.selectCurrentGroupName(state.groups);
  } else {
    groupName = fromAuthentication.getGroupName(state.authentication);
  }
  return groupName;
};

export const getGroupId = (state) =>
  fromGroups.selectCurrentGroupId(state.groups);

export const getInHouseResource = (state) =>
  fromGroups.selectInHouseResource(state.groups);

export const getSectionByRecordId = (state, record_id) =>
  fromEnrichmentCenter.getSectionByRecordId(state.enrichment_center, record_id);

export const getFilteredContent = (state) =>
  fromEnrichmentCenter.getFilteredContent(state.enrichment_center);

export const getSectionNameByRecordId = (state, record_id) =>
  fromEnrichmentCenter.getSectionNameByRecordId(state.enrichment_center, record_id);

export const getOrderOfContentInCategory = (state, record_id) =>
  fromEnrichmentCenter.getOrderOfContentInCategory(state.enrichment_center, record_id);

export const contentByCategory = (state) =>
  fromEnrichmentCenter.contentByCategory(state.enrichment_center);

export const getContentBySection = (state, section) =>
  fromEnrichmentCenter.getContentBySection(state.enrichment_center, section);

export const getContentRecordId = (state, section, lesson_number) =>
  fromEnrichmentCenter.getContentRecordId(state.enrichment_center, section, lesson_number);

export const getContentAndDeeperWider = (state, record_id) =>
  fromEnrichmentCenter.getContentAndDeeperWider(state.enrichment_center, record_id);

export const getSectionsWithContent = (state) =>
  fromEnrichmentCenter.getSectionsWithContent(state.enrichment_center);

export const getSpeakersWithContent = (state) =>
  fromEnrichmentCenter.getSpeakersWithContent(state.enrichment_center);

export const getStressorsWithContent = (state) =>
  fromEnrichmentCenter.getStressorsWithContent(state.enrichment_center, state.stressor_categories.categories.map(s => s.name));

export const getNumberOfActiveFilters = (state) =>
  fromEnrichmentCenter.getNumberOfActiveFilters(state.enrichment_center);

export const getSectionName = (state, section) =>
  fromEnrichmentCenter.getSectionName(state.enrichment_center, section);